// Public Compass demo storage. Deliberately isolated from draftStore.js:
// it never reads or writes the real draft keys, never calls the API, and
// holds only a fictional ranch dressing example.
import { EMPTY_PROGRESS, normalizeCompassProgress } from "./progressRules";

export const DEMO_STORAGE_KEY = "alaine_compass_public_demo_v1";
export const DEMO_SHARE_PATH = "/compass/demo";
export const DEMO_INTERACTIVE_PHASES = [1, 2, 3];

export function demoShareUrl() {
  const base = (process.env.PUBLIC_URL || "").replace(/\/$/, "");
  return `${window.location.origin}${base}${DEMO_SHARE_PATH}`;
}

export function createDemoSeed() {
  return {
    sampleVersion: 2,
    progress: normalizeCompassProgress({
      ...EMPTY_PROGRESS,
      productName: "Hollow Creek Buttermilk Ranch (fictional)",
      profileId: "dressing",
      completedPhases: [1],
      checklists: { 1: [0, 1, 2, 3], 2: [0, 1] },
      phaseRecords: {
        1: { noUnresolvedQuestions: true },
        2: { questionsDetails: "Does the herb blend settle in the bottle after 2 weeks?", questionsOwner: "Founder (sample)" },
      },
    }),
    drafts: {
      "savory-profile-intake": {
        "Product profile: dry seasoning, sauce, dip, salsa, dressing, frozen, snack, or meal kit": "Dressing - creamy buttermilk ranch",
        "Storage type: shelf-stable, refrigerated, frozen, or made-to-order": "Refrigerated (assumption to confirm with a process authority)",
        "First sales channel: DTC, retail, foodservice, farmers market, wholesale, or distributor": "Farmers market first, then two local grocers",
        "Target package size and serving/use occasion": "12 oz net weight (not fluid ounces); salads, wings, dipping. Package capacity and density still need confirmation.",
        "Known allergens or sensitive ingredients": "Milk, egg",
        "Claims the founder wants to make": "Small batch, no artificial colors (claim wording to be reviewed)",
        "What is already known and what is still unknown": "Known: flavor target. Unknown: shelf life, process requirements.",
        "Next decision needed before formulation work": "Convert kitchen recipe to grams and fix herb ratio",
        "brief-sku": "HC-RANCH-12; fictional refrigerated dressing for home use",
        "brief-benchmark": "Creamy pourable ranch for salads and dipping; benchmark to be selected before trials.",
        "brief-constraints": "Milk and egg accepted in this fictional brief. Ingredient restrictions and processing requirements need agreement.",
        "brief-targets": "Illustrative 12 oz net weight; sensory criteria, nutrition targets, cost target and shelf life remain to be agreed, not validated.",
        "brief-success": "Founder to agree a sensory scorecard; qualified reviewers to establish safety, label and release criteria before launch.",
        "brief-regulatory": "Refrigeration is a planning assumption. Qualified process and label reviewers not yet appointed.",
        "brief-feasibility": "Confirm cold-fill capability, package compatibility and minimum batch with a co-man before committing to packaging.",
      },
      "commercial-formula-method-sheet": {
        "Formula version and date": "v0.3 sample",
        "Ingredient list by weight and percentage": "Mayonnaise 42%, buttermilk 38%, sour cream 12%, vinegar 4%, herb and spice blend 3.2%, salt 0.8% (fictional, not a validated recipe)",
        "Supplier name and spec needed for each ingredient": "Placeholder suppliers; spec sheets not yet requested",
        "Batch method: order of addition, mix times, temperatures": "Whisk dry blend into buttermilk, fold into base, chill (sample only)",
        "Critical points: what ruins the batch if done wrong": "",
        "Target yield and expected loss": "",
        "formula-batch": "600 oz input mass = approximately 17,009.7 g; illustrative 4% physical loss yields 576 oz = 48 units × 12 oz net weight. Not a density or fluid-volume conversion.",
        "formula-reconcile": "Cost table includes all six ingredients in matching proportions: 252 oz mayonnaise, 228 oz buttermilk, 72 oz sour cream, 24 oz vinegar, 19.2 oz herb blend and 4.8 oz salt.",
      },
      "first-run-costing-worksheet": {
        "Product profile and storage type": "Dressing, refrigerated",
        "Target package size and units per case": "12 oz net weight, 12 per case; bottle capacity to be confirmed",
      },
      "pricing-margin-workbook": {
        "pm-product-name": "Buttermilk Ranch 12 oz (sample)",
        "pm-units-per-batch": "48",
        "pm-current-price": "7.50",
        "pm-proposed-price": "8.75",
        "pm-current-monthly-units": "310",
        "pm-volume-drop": "6",
        "pm-target-margin": "50",
        "pm-ingredients": [
          { name: "Mayonnaise", cost: "18.40", size: "128", unit: "oz (weight)", used: "252" },
          { name: "Buttermilk", cost: "4.29", size: "64", unit: "oz (weight)", used: "228" },
          { name: "Sour cream", cost: "6.87", size: "48", unit: "oz (weight)", used: "72" },
          { name: "Vinegar", cost: "3.00", size: "32", unit: "oz (weight)", used: "24" },
          { name: "Herb and spice blend", cost: "13.60", size: "16", unit: "oz (weight)", used: "19.2" },
          { name: "Salt", cost: "1.20", size: "26", unit: "oz (weight)", used: "4.8" },
        ],
        "pm-packaging": [
          { name: "Squeeze bottle", cost: "64.80", pieces: "120", used: "1" },
          { name: "Cap", cost: "21.60", pieces: "120", used: "1" },
          { name: "Label", cost: "38.00", pieces: "250", used: "1" },
        ],
        "pm-hours-per-batch": "3.5",
        "pm-labor-rate": "22",
        "pm-waste": "0",
        "pm-samples-month": "36",
        "pm-other-per-batch": "27",
        "pm-channels": [
          { name: "Farmers market", units: "180", price: "8.75", fees: "3" },
          { name: "Online / direct", units: "40", price: "8.75", fees: "11" },
          { name: "Local grocer", units: "90", price: "5.25", fees: "0" },
        ],
        "pm-fixed-rent": "340",
        "pm-fixed-insurance": "62",
        "pm-fixed-software": "29",
        "pm-fixed-marketing": "75",
        "pm-fixed-admin": "150",
        "pm-fixed-other": "0",
        "pm-decision": "Test $8.75 at market; rework grocer price before scaling",
        "cost-basis": "Initial estimate",
        "cost-source": "All prices are fictional planning assumptions, not supplier quotes. All quantities are weight. 600 oz input, 4% physical loss, 48 sellable 12 oz units. The loss is already in yield, so additional contingency is 0%. Confirm real batch yield, package capacity and costs before use.",
        "cost-development": "Not yet estimated; obtain development, initial testing and regulatory-review quotes separately.",
      },
    },
    savedAt: null,
  };
}

export function storageAvailable() {
  try {
    const probe = `${DEMO_STORAGE_KEY}__probe`;
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

export function loadDemoState() {
  try {
    const raw = window.localStorage.getItem(DEMO_STORAGE_KEY);
    if (!raw) return createDemoSeed();
    const parsed = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object") return createDemoSeed();
    const state = {
      progress: normalizeCompassProgress(parsed.progress),
      drafts: parsed.drafts && typeof parsed.drafts === "object" ? parsed.drafts : {},
      savedAt: parsed.savedAt || null,
      sampleVersion: parsed.sampleVersion || 1,
      sampleUpdateNotice: parsed.sampleUpdateNotice,
    };
    if (state.sampleVersion < 2) {
      const seed = createDemoSeed();
      // Replace only exact old sample defaults. Never replace customized inputs.
      const oldDefaults = {
        "savory-profile-intake": { "Target package size and serving/use occasion": "12 fl oz squeeze bottle; salads, wings, dipping" },
        "first-run-costing-worksheet": { "Target package size and units per case": "12 fl oz, 12 per case" },
        "pricing-margin-workbook": {
          "pm-waste": "4",
          "pm-ingredients": [
            { name: "Mayonnaise", cost: "18.40", size: "128", unit: "oz", used: "96" },
            { name: "Buttermilk", cost: "4.29", size: "64", unit: "oz", used: "88" },
            { name: "Sour cream", cost: "6.87", size: "48", unit: "oz", used: "28" },
            { name: "Herb and spice blend", cost: "13.60", size: "16", unit: "oz", used: "7" },
          ],
        },
      };
      for (const [id, old] of Object.entries(oldDefaults)) {
        const draft = state.drafts[id];
        if (!draft) continue;
        for (const [key, value] of Object.entries(old)) {
          if (JSON.stringify(draft[key]) === JSON.stringify(value)) draft[key] = seed.drafts[id][key];
        }
      }
      state.sampleVersion = 2;
      state.sampleUpdateNotice = "The sample now accounts for vinegar, salt, matching batch quantities and net weight. Customized entries were retained: reconcile your formula, yield and costs, or reset for a fully matched example.";
      state.progress.readinessReconfirmationRequired = [...new Set([
        ...state.progress.readinessReconfirmationRequired,
        ...state.progress.completedPhases.filter((n) => n >= 2),
      ])];
      try { window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state)); } catch { /* retain in memory */ }
    }
    return state;
  } catch {
    return createDemoSeed();
  }
}

export function saveDemoState(state) {
  try {
    window.localStorage.setItem(DEMO_STORAGE_KEY, JSON.stringify(state));
    return { ok: true };
  } catch {
    return { ok: false };
  }
}

// Removes only the demo key and returns a fresh seed.
export function resetDemoState() {
  try { window.localStorage.removeItem(DEMO_STORAGE_KEY); } catch { /* unavailable */ }
  return createDemoSeed();
}

export async function copyDemoLink() {
  const url = demoShareUrl();
  try {
    if (!navigator.clipboard?.writeText) throw new Error("Clipboard unavailable");
    await navigator.clipboard.writeText(url);
    return { ok: true, url };
  } catch {
    return { ok: false, url };
  }
}
